import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { Observable } from 'rxjs';
import { switchMap, take, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagGuard implements CanActivate {
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.authService.fetchAndCacheRoles(false).pipe(
      switchMap(() => this.authService.getUserObj()),
      take(1),
      map((userObj) => {
        // Super flag check — if disabled, all QRS features are disabled
        if (!userObj?.ENABLE_QRS_PROGRAM_FEATURE) {
          this.router.navigate(['/denied']);
          return false;
        }

        // Role-based access check
        const allowedRoles = route.data?.['allowedRoles'] as string[] | undefined;
        if (allowedRoles && allowedRoles.length > 0) {
          const userRoles = (userObj?.roleInfo || []).map((r: any) => r.UR_ROLE);
          const hasAccess = allowedRoles.some((allowedRole: string) => {
            if (allowedRole.includes('+')) {
              // Composite role: user must have ALL roles in the combination
              const requiredRoles = allowedRole.split('+').map((r: string) => r.trim());
              return requiredRoles.every((role: string) => userRoles.includes(role));
            }
            // Single role: check primary ROLE or roleInfo
            return userObj?.ROLE === allowedRole || userRoles.includes(allowedRole);
          });
          if (!hasAccess) {
            this.router.navigate(['/denied']);
            return false;
          }
        }

        // Feature flag check
        const requiredFlag = route.data?.['featureFlag'] as string;

        if (!requiredFlag) {
          return true;
        }

        if (userObj[requiredFlag] === true) {
          return true;
        }

        this.router.navigate(['/denied']);
        return false;
      })
    );
  }
}
